import React from 'react';
import classNames from 'classnames';
import Immutable from 'immutable';
import { useSelector } from 'react-redux';

import { useTranslation } from 'react-i18next';
import { Paragraph, DynamicMediaItem } from '@uncinc/react-drupal-core';
import { Container, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';
import {
  ImmutableEntity,
  ImmutableRootState,
} from '@uncinc/react-drupal-core/types';

import { NodeVacancy } from '../../../../types/uncinc_profile/nodes';
import {
  dateFormatter,
  getOverviewUrlFromMenu,
} from '../../../../helpers';

import BackButton from '../../../../components/BackButton';
import ShareWrapper from '../../../../components/ShareWrapper';

import './style.scss';

interface NodeVacancyProps extends React.ComponentPropsWithoutRef<'div'> {
  entity: ImmutableEntity<NodeVacancy>;
}

const NodeVacancyPage: React.FC<NodeVacancyProps> = ({ entity }) => {
  const classes = classNames('Node NodeVacancyPage');
  const vacancy = entity.toJS();
  const title = vacancy.title[0].value;
  const image = vacancy?.field_media_item[0];
  const paragraphs = vacancy.field_paragraphs;
  const body = vacancy.body[0]?.processed;
  const date = vacancy.field_publish_date[0]?.value || vacancy.created[0]?.value;
  const lang = 'nl';
  const { t } = useTranslation();

  const footerMenu = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['menu', 'footer']));
  const overviewUrl = getOverviewUrlFromMenu(footerMenu, 'vacancies');

  return (
    <article itemType="http://schema.org/WebPage" className={classes}>
      <Container>
        <BackButton
          text={t('content_types.vacancy.overview')}
          to={overviewUrl}
        />
      </Container>
      <Container small>
        <div className="NodeVacancyPage__hero">
          <div className="NodeVacancyPage__hero__meta">
            <p className="NodeVacancyPage__hero__meta__type">
              {t('content_types.vacancy.type')}
            </p>
            <span className="dot">&bull;</span>
            <p>{dateFormatter(date, lang)}
            </p>
          </div>
          <div className="NodeVacancyPage__hero__text">
            <h1 itemProp="name">{title}</h1>
            {body ? (
              <WysiwygField
                property="schema:text"
                html={body}
              />
            ) : null}
          </div>
          <div className="NodeVacancyPage__hero__image">
            {image && (
              <DynamicMediaItem
                media={image}
                imageAspectRatio="16_9"
                caption={image.entity.field_caption[0]?.value}
              />
            )}
          </div>
        </div>
      </Container>

      {paragraphs.length !== 0 ? (
        <div className="NodeVacancyPage__paragraphs">
          {paragraphs.map((paragraph) => {
            const id = paragraph.target_id;
            const language = paragraph.entity.langcode[0].value;
            return (
              <Paragraph
                key={`paragraph:${id}:${language}`}
                entity={Immutable.fromJS(paragraph.entity)}
              />
            );
          })}
        </div>
      ) : null}

      { /* Social Share Block */}
      <ShareWrapper />

    </article>
  );
};

export default NodeVacancyPage;
